import { Drawer, DrawerContent, DrawerDescription, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, Loader2, MapPin, Search } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { DatePanel } from './date-panel';
import { DestinationPanel } from './destination-panel';
import { CTA, TRIGGER } from './search-bar.styles';
import { useSearchBarContext } from './use-search-bar';

interface MobileSheetProps {
  mobileTrigger: 'floating' | 'inline';
  className?: string;
}

/**
 * The mobile surface: one sheet, two steps.
 *
 * The original flow stacked three overlays — a sheet, a drawer nested inside it
 * for the location, and a popover for the calendar — which cost six taps and
 * opened onto two empty rows. This keeps the single overlay but steps through
 * destination → date: with a full destination list the date rail sat well below
 * the fold, so showing both at once meant the second choice was never visible.
 * Picking a destination advances automatically, and the footer summary keeps the
 * choice on screen with one tap back to change it.
 *
 * Built on vaul's Drawer rather than shadcn's Sheet: SheetContent hard-codes a
 * close button at top-right that can't be removed without forking the primitive.
 */
export function MobileSheet({ mobileTrigger, className }: MobileSheetProps) {
  const {
    isSheetOpen,
    sheetStep,
    openSheet,
    close,
    backToDestination,
    selectedSite,
    dateValue,
    submit,
    isSubmitting,
    submitError,
    announcement,
  } = useSearchBarContext();

  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isDateStep = sheetStep === 'date';

  const dateLabel = dateValue ? format(parseISO(dateValue), 'dd/MM/yyyy') : 'Thêm ngày';
  const destinationLabel = selectedSite?.name ?? 'Bạn muốn đi đâu?';

  /**
   * Move focus to the new step's heading on advance.
   *
   * Left alone, the outgoing panel unmounts, focus falls to <body>, and vaul's
   * trap pulls it to the first focusable — which is the back button, so a screen
   * reader announces the *undo* affordance as the result of choosing a
   * destination. The heading is the honest landing point.
   */
  useEffect(() => {
    if (!isSheetOpen) return;
    stepHeadingRef.current?.focus({ preventScroll: true });
  }, [isSheetOpen, sheetStep]);

  const trigger =
    mobileTrigger === 'inline' ? (
      <button type="button" className={TRIGGER.inline}>
        <span className="min-w-0 flex-1">
          <span className={cn('block truncate text-sm font-semibold', !selectedSite && 'text-muted-foreground font-normal')}>
            {destinationLabel}
          </span>
          <span className="text-muted-foreground block truncate text-xs">{dateLabel}</span>
        </span>
        <span className={TRIGGER.icon} aria-hidden="true">
          <Search className="size-4.5" />
        </span>
      </button>
    ) : (
      <button type="button" className={cn(TRIGGER.floating, isSheetOpen && 'hidden')}>
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold">{selectedSite?.name ?? 'Tìm kiếm'}</span>
          <span className="text-muted-foreground block truncate text-xs">{dateLabel}</span>
        </span>
        <span className={cn(TRIGGER.icon, 'size-9')} aria-hidden="true">
          <Search className="size-4" />
        </span>
      </button>
    );

  return (
    <div className={className}>
      <Drawer open={isSheetOpen} onOpenChange={(open) => (open ? openSheet() : close())}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>

        {/* svh, not vh — vh doesn't account for the iOS URL bar and the sticky
            footer ends up below the fold. */}
        <DrawerContent className="h-[76svh] max-h-[76svh]">
          <div className="flex h-full min-h-0 flex-col">
            <header className="flex items-center gap-2 px-4 pt-1 pb-3">
              {isDateStep && (
                <button
                  type="button"
                  onClick={backToDestination}
                  aria-label={selectedSite ? `Đổi điểm đến, hiện tại ${selectedSite.name}` : 'Quay lại chọn điểm đến'}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-primary-ink -ml-1.5 rounded-full p-1.5 transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                  <ChevronLeft className="size-5" />
                </button>
              )}
              {/* tabIndex -1 so the step-change effect can land focus here. */}
              <DrawerTitle ref={stepHeadingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
                {isDateStep ? 'Chọn ngày đi' : 'Bạn muốn đi đâu?'}
              </DrawerTitle>
              <DrawerDescription className="sr-only">
                {isDateStep ? `Chọn ngày cho ${selectedSite?.name ?? 'điểm đến đã chọn'}` : 'Chọn điểm đến để tiếp tục'}
              </DrawerDescription>
            </header>

            {/* Keyed on the step so the incoming panel animates rather than swapping
                in place, and so the calendar isn't built until the date step opens. */}
            <div
              key={sheetStep}
              className="animate-in fade-in slide-in-from-right-2 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-4 duration-200 motion-reduce:animate-none"
            >
              {isDateStep ? <DatePanel variant="sheet" /> : <DestinationPanel variant="sheet" />}
            </div>

            <footer className="safe-area-pb border-pill-border border-t bg-white px-4 pt-3 pb-3">
              {submitError && (
                <p role="alert" className="text-destructive mb-2 text-sm">
                  {submitError}
                </p>
              )}

              {/* The chosen destination stays on screen through the date step, and
                  doubles as the way back — the header arrow is easy to miss with a
                  thumb on the CTA. */}
              {isDateStep && selectedSite && (
                <button
                  type="button"
                  onClick={backToDestination}
                  className="border-pill-border focus-visible:ring-primary-ink mb-2 flex w-full items-center gap-2 rounded-chip border bg-white px-3 py-2 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none motion-reduce:transition-none [@media(hover:hover)]:hover:bg-pill"
                >
                  <MapPin className="text-primary size-4 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{selectedSite.name}</span>
                    <span className="text-muted-foreground block truncate text-xs">{dateLabel}</span>
                  </span>
                  <span className="text-primary-ink shrink-0 text-xs font-semibold">Đổi</span>
                </button>
              )}

              <button type="button" onClick={submit} disabled={isSubmitting} aria-busy={isSubmitting} className={cn(CTA.base, 'h-12 w-full')}>
                {isSubmitting ? (
                  <Loader2 className="size-4.5 animate-spin motion-reduce:animate-none" />
                ) : (
                  <Search className="size-4.5" />
                )}
                <span>{isSubmitting ? 'Đang tìm…' : 'Tìm kiếm'}</span>
              </button>
            </footer>
          </div>

          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {announcement}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
